import cx from 'classnames'
import { useRef, useState } from 'react'

import type { AccordionVariant } from '..'
import { Accordion } from '..'
import styles from './accordion-gallery.module.css'

const VARIANTS: AccordionVariant[] = ['white', 'light-2', 'dark']

const Media = () => (
  <div className={styles.media} aria-hidden="true">
    16:9 image
  </div>
)

const ExampleItems = () => (
  <>
    <Accordion.Item
      title="What's included in a full service?"
      iconName="shield"
      expanded
    >
      <p className={styles.copy}>
        Example content: an oil and filter change, plus checks on brakes, tyres,
        lights, steering and suspension, with a report on anything that needs
        attention.
      </p>
      <Media />
    </Accordion.Item>
    <Accordion.Item
      title="Can I move my booking to a different date or service centre after it has been confirmed?"
      iconName="calendar"
    >
      <p className={styles.copy}>
        Example content: yes. Use the link in your confirmation email to pick
        another date or centre.
      </p>
    </Accordion.Item>
    <Accordion.Item title="Do I need to bring anything?">
      <p className={styles.copy}>
        Example content: your booking confirmation lists what to bring.
      </p>
    </Accordion.Item>
  </>
)

/**
 * Development-only gallery rendering every Accordion variant.
 *
 * Mirrors the Figma component set so the implementation can be compared
 * against the design side by side. Figma's S and L are the mobile and desktop
 * layouts, so resize the window across 1024px to compare them.
 *
 * @see https://www.figma.com/design/uk4HuDPdSGgZMSi3iybv5V/Servicing-Landing-Pages?node-id=3109-51886
 */
export const AccordionGallery = () => {
  const [showOutlines, setShowOutlines] = useState(false)
  const [lastChange, setLastChange] = useState('nothing yet')
  const pageRef = useRef<HTMLElement>(null)

  // Shows that onOpenChange fires, and with which state, without a console.
  const reportChange = (label: string) => (isOpen: boolean) =>
    setLastChange(`${label} → ${isOpen ? 'open' : 'closed'}`)

  // `expanded` only sets the state an item starts in, so clicking is the only
  // way to move items already on screen. There is no "open every item": opening
  // an item closes the others in its accordion. Disabled items are left out:
  // their triggers ignore clicks, and the States section shows them as they are.
  const closeEveryItem = () => {
    pageRef.current
      ?.querySelectorAll<HTMLButtonElement>(
        'button[aria-expanded="true"]:not([aria-disabled])',
      )
      .forEach((button) => button.click())
  }

  return (
    <main
      ref={pageRef}
      className={cx(styles.page, showOutlines && styles.outlined)}
    >
      <header className={styles.header}>
        <p className={styles.eyebrow}>Design System V3</p>
        <h1 className={styles.title}>Accordion</h1>
        <p className={styles.lede}>
          Every variant the component supports: {VARIANTS.length} variants, each
          with its first item open, plus the states an item can start in.
          Opening an item closes the others in its accordion, except disabled
          ones. Size is not a prop: Figma's S applies below 1024px and L from
          1024px up, so resize the window to compare them.
        </p>
        <p className={styles.viewport}>
          Current size:{' '}
          <strong className={styles.sizeS}>S (viewport below 1024px)</strong>
          <strong className={styles.sizeL}>L (viewport 1024px and up)</strong>
        </p>
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.button}
            onClick={closeEveryItem}
          >
            Close every item
          </button>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={showOutlines}
              onChange={(event) => setShowOutlines(event.target.checked)}
            />
            Outline each item, its header parts and its panel
          </label>
        </div>
      </header>

      {VARIANTS.map((variant) => (
        <section key={variant} className={styles.section}>
          <h2 className={styles.sectionTitle}>
            variant=<code className={styles.code}>{variant}</code>
          </h2>
          <div className={styles.frame}>
            <Accordion variant={variant}>
              <ExampleItems />
            </Accordion>
          </div>
        </section>
      ))}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>States</h2>
        <p className={styles.frameLabel}>
          Last onOpenChange: {lastChange}. Disabled items report nothing.
        </p>
        <div className={styles.pair}>
          {VARIANTS.map((variant) => (
            <div key={variant} className={styles.frame}>
              <p className={styles.frameLabel}>
                variant=<code className={styles.code}>{variant}</code>: open on
                load, disabled, and disabled while open
              </p>
              <Accordion variant={variant}>
                <Accordion.Item
                  expanded
                  title="Starts expanded"
                  iconName="shield"
                  onOpenChange={reportChange(`${variant}: Starts expanded`)}
                >
                  <p className={styles.copy}>
                    Example content: expanded sets the state this item starts
                    in, and it can still be closed.
                  </p>
                </Accordion.Item>
                <Accordion.Item
                  isDisabled
                  title="Disabled"
                  iconName="calendar"
                  onOpenChange={reportChange(`${variant}: Disabled`)}
                >
                  <p className={styles.copy}>
                    Example content: never reachable, the trigger ignores clicks
                    and keys.
                  </p>
                </Accordion.Item>
                <Accordion.Item
                  expanded
                  isDisabled
                  title="Disabled while open"
                  onOpenChange={reportChange(`${variant}: Disabled while open`)}
                >
                  <p className={styles.copy}>
                    Example content: disabling an open item leaves its content
                    readable, even after another item opens.
                  </p>
                </Accordion.Item>
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Edge cases</h2>
        <div className={styles.pair}>
          <div className={styles.frame}>
            <p className={styles.frameLabel}>
              An item without content renders nothing: only the second item
              shows
            </p>
            <Accordion variant="light-2">
              <Accordion.Item title="This item has no content" />
              <Accordion.Item title="The only item">
                <p className={styles.copy}>Example content.</p>
              </Accordion.Item>
            </Accordion>
          </div>
          <div className={styles.frame}>
            <p className={styles.frameLabel}>
              Consumer class names merge on the root and the item
            </p>
            <Accordion variant="dark" className={styles.consumerRoot}>
              <Accordion.Item
                title="Rounded accordion, dashed divider"
                iconName="tick"
                className={styles.consumerItem}
              >
                <p className={styles.copy}>Plain content, no class needed.</p>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>
      </section>
    </main>
  )
}
